/* eslint-disable */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Grid, IconButton, Skeleton,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { CustomTable } from 'Shared/Table';
import TablePagination from 'Shared/Table/Pagination';
import { SingleDatePicker } from 'Shared/UI/DateRangePicker';
import DropdownSelect from 'Shared/UI/DropdownSelect';
import searchIcon from 'Images/search_icon.svg';
import pictureIcon from 'Images/picture_image.svg';
import { ReactComponent as UpdateIcon } from 'Images/update_icon.svg';
import { ReactComponent as DeleteIcon } from 'Images/delete_icon.svg';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { format } from 'date-fns';
import withRoles from 'HOC/withRoles';
import useNetworkLoader from 'hooks/useNetworkLoader';
import AppButton from 'Shared/UI/Button';
import ConfirmDialog from 'Shared/ConfirmDialog';
import useDebounce from 'hooks/useDebounce';
import toast from 'Utils/toast';
import { getProductsData, getBrands } from 'Components/Catalog/CatalogServices';
import { getFilterCounts } from 'Components/Catalog/SearchService';
import { getCaterogyData } from 'Components/Home/HomeServices';
import { EditorState, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import {
  addAnnouncements, addAttachement, deleteAnnouncements, getAnnouncements,
} from './Services';
import { StyledField } from 'Components/AccountSettings/helpers';
import { ANNOUCE_TYPE } from './constants';

/* ── Type configuration ─────────────────────────────────────── */
const TYPE_OPTIONS = [
  { value: 'NOUVEAUTE', label: 'Nouveauté' },
  { value: 'PROMOTION', label: 'Promotion' },
  { value: 'INFORMATION', label: 'Information' },
  { value: 'EVENT', label: 'Évènement/Autres' },
];

const TYPE_COLORS = {
  NOUVEAUTE: { bg: '#0AD69F1D', color: '#0AD69F', dot: '#0AD69F',border:'#0AD69F' },
  PROMOTION: { bg: '#F7ECED', color: '#E56B6F', dot:'#E56B6F',border:'#E56B6F' },
  INFORMATION: { bg: '#027BC614', color: '#007BC6', dot: '#007BC6',border:'#007BC6' },
  EVENT: { bg: '#F3E6FF', color: '#8E44AD', dot: '#8E44AD',border:'#8E44AD' },
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_COLORS[type] || { bg: '#F0F4FA', color: '#4A5568', dot: '#A0AEC0' };
  const lbl = TYPE_OPTIONS.find((t) => t.value === type)?.label || type;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      backgroundColor: cfg.bg, borderRadius: '20px', px: '10px', py: '4px',
      border: `1px solid ${cfg.border || cfg.color}`,
    }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.dot }} />
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: cfg.color }}>{lbl}</Typography>
    </Box>
  );
};

/* ── Drag-and-drop upload zone ──────────────────────────────── */
const UploadZone = ({
  label, hint, file, fileUrl, onChange, onClear, accept = 'image/*', icon: Icon,
  warnMB = 2, maxMB = 30,
}) => {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const [sizeMsg, setSizeMsg] = useState(null); // { text, warn } or null
  const fileName = file?.name || (typeof file === 'string' ? file : '');
  const openUrl = fileUrl || null;

  const handleFile = (f) => {
    const mb = f.size / 1024 / 1024;
    if (mb > maxMB) {
      setSizeMsg({ text: `Fichier trop volumineux (${mb.toFixed(1)} MB — max ${maxMB} MB)`, warn: false });
      return;
    }
    if (mb > warnMB) {
      setSizeMsg({ text: `Image volumineuse (${mb.toFixed(1)} MB) — le chargement sera lent pour les utilisateurs`, warn: true });
    } else {
      setSizeMsg(null);
    }
    onChange(f);
  };

  return (
    <Box>
    <Box
      onClick={() => !fileName && ref.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files[0];
        if (f) handleFile(f);
      }}
      sx={{
        border: `2px dashed ${drag ? '#2557A7' : '#DEE5EE'}`,
        borderRadius: '8px',
        px: '16px',
        cursor: fileName ? 'default' : 'pointer',
        backgroundColor: drag ? '#EBF4FF' : '#ffffff',
        transition: 'all 0.2s',
        '&:hover': !fileName ? { borderColor: '#2557A7', backgroundColor: '#EBF4FF' } : {},
        height: '62px',
        minHeight: 'unset',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 22, color: '#8397AE', flexShrink: 0 }} /> : <img src={pictureIcon} alt="upload" style={{ width: 30, height: 26, flexShrink: 0 }} />}
      {fileName ? (
        <>
          <Typography
            {...(openUrl ? { component: 'a', href: openUrl, target: '_blank', rel: 'noreferrer' } : {})}
            onClick={(e) => openUrl && e.stopPropagation()}
            sx={{
              fontSize: '13px', fontWeight: 600, color: '#2557A7',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '60%',
              textDecoration: openUrl ? 'underline' : 'none',
              cursor: openUrl ? 'pointer' : 'default',
            }}
          >
            {fileName}
          </Typography>
          {onClear && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); ref.current.value = ''; setSizeMsg(null); onClear(); }}
              sx={{ color: '#EF456E', p: '2px', flexShrink: 0, '&:hover': { backgroundColor: '#FFF0F3' } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: '13px', color: '#8397AE', lineHeight: 1.3, textAlign: 'center' }}>
          {label}
          <br />
          ou{' '}
          <span style={{ fontSize: '13px', color: '#027BC6', fontWeight: 600 }}>parcourez</span>
          {' '}
          <span style={{ fontSize: '13px', color: '#8397AE' }}>
            pour choisir un fichier{hint ? ` (${hint})` : ''}
          </span>
        </Typography>
      )}
      <input
        ref={ref}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
      />
    </Box>
    {sizeMsg && (
      <Typography sx={{ fontSize: '11px', mt: '4px', px: '4px', color: sizeMsg.warn ? '#B45309' : '#DC2626', fontWeight: 500 }}>
        {sizeMsg.text}
      </Typography>
    )}
    </Box>
  );
};

/* ── Styled field (matches account-settings floating label) ─── */
const Field = ({
  label, value, onChange, type = 'text', disabled, multiline, rows, required,
}) => (
  <StyledField
    label={required ? `${label} *` : label}
    value={value || ''}
    onChange={onChange}
    type={type}
    disabled={disabled}
    multiline={multiline}
    rows={rows}
  />
);

/* ── PROMOTION helpers ──────────────────────────────────────── */
const APPLY_TO_OPTIONS = [
  { value: 'Produits', label: 'Produits' },
  { value: 'Catégories', label: 'Catégories' },
  { value: 'Marque', label: 'Marque' },
];

const ITEM_BADGE = {
  Produit: { bg: '#0AD69F1D', color: '#0AD69F', border: '#0AD69F'  },
  Catégorie: { bg: '#007BC62E', color: '#007BC6',border:'#007BC6' },
  Marque: { bg: '#9D5BEF17', color: '#8050BB',border:'#8050BB' },
};

const SelectedItemsPanel = ({ items, onRemove }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {items.map((item, i) => (
      <Box
        key={i}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          border: '1px solid #E2E8F0', borderRadius: '8px', px: '12px', py: '10px',
          minHeight: '72px',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, mr: '8px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000', mb: '4px', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {item.name}
          </Typography>
          <Box sx={{
            display: 'inline-flex', px: '8px', py: '2px',
            borderRadius: '12px', backgroundColor: ITEM_BADGE[item.type]?.bg,
            border: ITEM_BADGE[item.type]?.border ? `1px solid ${ITEM_BADGE[item.type].border}` : 'none',
          }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: ITEM_BADGE[item.type]?.color }}>
              {item.type}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={() => onRemove(i)} sx={{ color: '#EF456E', flexShrink: 0 }}>
          <DeleteIcon style={{ width: 16, height: 16 }} />
        </IconButton>
      </Box>
    ))}
  </Box>
);

/* ════════════════════════════════════════════════════════════
   ANNOUNCEMENT FORM
   ════════════════════════════════════════════════════════════ */
const initialFormValues = {
  title: '',
  type: '',
  startDate: '',
  endDate: '',
  remise: '',
  link: '',
  shortDescription: '',
  description: { editorState: EditorState.createEmpty() },
  image: null,
  fileAttachment: null,
  productToggle: ANNOUCE_TYPE.PRODUCT,
  applyTo: '',
};

const AnnouncementForm = ({
  editItem, onBack, onSaved,
}) => {
  const { callApi } = useNetworkLoader();
  const [form, setForm] = useState(editItem
    ? {
      ...initialFormValues,
      ...editItem,
      shortDescription: editItem.descriptionEvent || editItem.description || '',
      link: editItem.linkEvent || '',
      fileAttachment: editItem.fileEvent || null,
      description: editItem.type === 'EVENT'
        ? { editorState: (() => { const cb = htmlToDraft(editItem.descriptionEvent || ''); return cb ? EditorState.createWithContent(ContentState.createFromBlockArray(cb.contentBlocks)) : EditorState.createEmpty(); })() }
        : { editorState: EditorState.createEmpty() },
      startDate: editItem.startDate?.split('T')[0] || '',
      endDate: editItem.endDate?.split('T')[0] || '',
    }
    : { ...initialFormValues });

  /* product selection state */
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState(() => {
    if (!editItem) return [];
    const refs = [...new Set([...(editItem.newProducts || []), ...(editItem.promoProducts || [])])];
    return refs.map((ref) => ({ id: ref, name: ref, type: 'Produit', ref }));
  });
  const [productSearch, setProductSearch] = useState('');
  const [contentSearch, setContentSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandCounts, setBrandCounts] = useState({});
  const [productPage, setProductPage] = useState(0);
  const [productPageSize, setProductPageSize] = useState(10);
  const [productTotal, setProductTotal] = useState(0);
  const [expandedCats, setExpandedCats] = useState({});
  const [productsLoading, setProductsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('reference_asc');
  const debouncedSearch = useDebounce(productSearch, 400);

  const set = (name) => (e) => setForm((p) => ({ ...p, [name]: e?.target ? e.target.value : e }));

  /* load products when PROMOTION/NOUVEAUTE and search/filters change */
  useEffect(() => {
    if (form.type !== 'PROMOTION' && form.type !== 'NOUVEAUTE') return;
    setProductsLoading(true);
    const [sortField, sortDir] = sortBy.split('_');
    callApi(
      () => getProductsData(`page=${productPage}&size=${productPageSize}&sort=${sortField},${sortDir}`, {
        search: debouncedSearch,
        families: catFilter ? [catFilter] : [],
        brands: brandFilter ? [brandFilter] : [],
      }),
      (data) => {
        setProducts(data?.content || []);
        setProductTotal(data?.totalPages || 1);
        setProductsLoading(false);
      },
      false,
    );
  }, [form.type, debouncedSearch, catFilter, brandFilter, productPage, productPageSize, sortBy]);

  /* load categories + brands */
  useEffect(() => {
    if (form.type !== 'PROMOTION' && form.type !== 'NOUVEAUTE') return;
    callApi(() => getCaterogyData(), (data) => {
      setCategories(data || []);
    }, false);
    callApi(() => getBrands(''), (data) => setBrands(data || []), false);
    callApi(() => getFilterCounts({ families: [], brands: [] }), (data) => {
      const map = {};
      (data?.brands || []).forEach((b) => { map[b.name] = b.count; });
      setBrandCounts(map);
    }, false);
  }, [form.type]);

  const isPromo = form.type === 'PROMOTION';
  const isNewOrPromo = isPromo || form.type === 'NOUVEAUTE';
  const [saving, setSaving] = useState(false);

  const handleSubmit = () => {
    if (!form.title) { toast.error('Veuillez saisir un titre'); return; }
    if (!form.type) { toast.error('Veuillez sélectionner un type'); return; }
    if (!form.startDate) { toast.error('Veuillez saisir une date de début'); return; }
    if (!form.endDate) { toast.error('Veuillez saisir une date de fin'); return; }

    setSaving(true);

    const done = () => setSaving(false);

    const doSave = (imageFileName, fileEventName) => {
      const payload = {
        announcementId: editItem?.id || editItem?.announcementId,
        title: form.title,
        type: form.type,
        startDate: format(new Date(form.startDate), "yyyy-MM-dd'T'00:00:00+0000"),
        endDate: format(new Date(form.endDate), "yyyy-MM-dd'T'23:59:59+0000"),
        image: typeof imageFileName === 'string' ? imageFileName : null,
        imageEvent: typeof imageFileName === 'string' ? imageFileName : null,
        remise: isNewOrPromo ? Number(form.remise) : 0,
        linkEvent: !isNewOrPromo ? (form.link || '') : '',
        descriptionEvent: isPromo ? '' : (form.shortDescription || ''),
        fileEvent: typeof fileEventName === 'string' ? fileEventName : null,
        productToggle: true,
        newProducts: form.type === 'NOUVEAUTE' ? selectedItems.filter((s) => s.type === 'Produit').map((s) => s.ref) : [],
        promoProducts: form.type === 'PROMOTION' ? selectedItems.filter((s) => s.type === 'Produit').map((s) => s.ref) : [],
        infoProducts: [],
        excludeNewProducts: [],
        excludePromoProducts: [],
        excludeInfoProducts: [],
        families: isNewOrPromo ? selectedItems.filter((s) => s.type === 'Catégorie').map((s) => s.id) : [],
        brands: isNewOrPromo ? selectedItems.filter((s) => s.type === 'Marque').map((s) => s.name) : [],
        all: {
          promo: form.type === 'PROMOTION',
          new: form.type === 'NOUVEAUTE',
          info: form.type === 'INFORMATION',
        },
      };

      callApi(() => addAnnouncements(payload), () => {
        toast.success(editItem ? 'Annonce modifiée avec succès' : 'Annonce créée avec succès');
        done();
        onSaved();
      }, true, done);
    };

    const uploadAndSave = (imageFileName) => {
      if (form.fileAttachment?.name) {
        addAttachement(form.fileAttachment)
          .then((res) => doSave(imageFileName, res.fileName))
          .catch(() => {
            toast.warning("La pièce jointe n'a pas pu être téléchargée.");
            doSave(imageFileName, null);
          });
      } else {
        const existingFile = typeof form.fileAttachment === 'string' ? form.fileAttachment : (editItem?.fileEvent || null);
        doSave(imageFileName, existingFile);
      }
    };

    if (form.image?.name) {
      addAttachement(form.image)
        .then((res) => uploadAndSave(res.fileName))
        .catch(() => {
          toast.warning("L'image n'a pas pu être téléchargée, l'annonce sera sauvegardée sans image.");
          uploadAndSave(null);
        });
    } else {
      uploadAndSave(typeof form.image === 'string' ? form.image : (editItem?.imageEvent || null));
    }
  };

  return (
    <Box sx={{ backgroundColor: '#F4F6FA', pb: '40px' }}>
      {/* Top bar */}
      <Box sx={{
        px: { xs: '16px', md: '40px' },
        py: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px',
      }}
      >
        {/* Left: breadcrumb + title */}
        <Box>
          <Box
            onClick={onBack}
            sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', mb: '8px' }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 11, color: '#8397AE' }} />
            <Typography sx={{ fontSize: '14px', color: '#8397AE', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              Retour à la liste des annonces
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
            {editItem ? "Modification d'une annonce" : "Création d'une nouvelle annonce"}
          </Typography>
        </Box>
        {/* Right: actions */}
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Button
            startIcon={<CloseIcon />}
            onClick={onBack}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '14px',
              color: '#385576', borderRadius: '8px',
              px: '16px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 5px 10px #626B7D12',
              border: 'none',
              '& .MuiButton-startIcon': { marginRight: '2px' },
              '&:hover': { backgroundColor: '#F8FAFC', boxShadow: '0px 5px 10px #626B7D12' },
            }}
          >
            Annuler
          </Button>
          <Button
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckIcon />}
            onClick={handleSubmit}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '14px',
              backgroundColor: '#007BC6', borderRadius: '8px', px: '20px',
              '& .MuiButton-startIcon': { marginRight: '3px' },
              '&:hover': { backgroundColor: '#005F9E' },
              '&.Mui-disabled': { backgroundColor: '#007BC6', opacity: 0.6, color: '#fff' },
            }}
          >
            {saving ? 'Enregistrement...' : 'Valider'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: { xs: '16px', md: '32px' }, pt: '8px' }}>
        {/* ── Section 1: Informations générales ── */}
        <Box sx={{
          backgroundColor: '#fff', border: '1px solid #D1D5DC',
          borderRadius: '12px', p: { xs: '20px', md: '16px' }, mb: '14px',
        }}
        >
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '-2px'}}>
            Informations générales
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#8397AE', fontWeight: 400, mb: '20px' }}>
            Ajouter des informations générales nécessaires
          </Typography>

          {/* Upload zones */}
          <Grid container spacing={2} sx={{ mb: '20px' }}>
            <Grid item xs={12} md={(form.type === 'EVENT' || form.type === 'INFORMATION') ? 6 : 12}>
              <UploadZone
                label="Faites glisser et déposez l'image"
                hint="PNG, JPG"
                file={form.image}
                fileUrl={editItem?.imageUrl || editItem?.imageEventUrl}
                onChange={(f) => setForm((p) => ({ ...p, image: f }))}
                onClear={() => setForm((p) => ({ ...p, image: null }))}
              />
            </Grid>
            {(form.type === 'EVENT' || form.type === 'INFORMATION') && (
              <Grid item xs={12} md={6}>
                <UploadZone
                  icon={AttachFileIcon}
                  label="Glissez-déposez votre fichier ici"
                  hint="PNG, JPG, PDF"
                  file={form.fileAttachment}
                  fileUrl={editItem?.fileEventUrl}
                  onChange={(f) => setForm((p) => ({ ...p, fileAttachment: f }))}
                  onClear={() => setForm((p) => ({ ...p, fileAttachment: null }))}
                  accept="image/png,image/jpeg,application/pdf"
                  warnMB={10}
                />
              </Grid>
            )}
          </Grid>

          {/* Form fields */}
          <Grid container spacing={2}>
            {/* Row 1: Titre | Type | Date début */}
            <Grid item xs={12} md={4}>
              <Field
                label="Titre de l'annonce"
                value={form.title}
                onChange={set('title')}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DropdownSelect
                options={TYPE_OPTIONS}
                value={form.type}
                onChange={(v) => set('type')({ target: { value: v } })}
                placeholder="Type de l'annonce *"
                height="52px"
                fontSize="14px"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SingleDatePicker
                value={form.startDate ? new Date(form.startDate) : null}
                onApply={(d) => set('startDate')({ target: { value: format(d, 'yyyy-MM-dd') } })}
                placeholder="Date début *"
                floatingLabel
                maxFutureYears={5}
              />
            </Grid>

            {/* Row 2: Date fin + conditional field */}
            <Grid item xs={12} md={4}>
              <SingleDatePicker
                value={form.endDate ? new Date(form.endDate) : null}
                onApply={(d) => set('endDate')({ target: { value: format(d, 'yyyy-MM-dd') } })}
                placeholder="Date fin *"
                floatingLabel
                maxFutureYears={5}
              />
            </Grid>
            {isNewOrPromo && (
              <Grid item xs={12} md={4}>
                <Field
                  label="Taux de remise"
                  value={form.remise}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { setForm((p) => ({ ...p, remise: '' })); return; }
                    const num = Math.min(100, Math.max(0, Number(raw)));
                    setForm((p) => ({ ...p, remise: num }));
                  }}
                  type="number"
                />
              </Grid>
            )}
            {!isNewOrPromo && (
              <Grid item xs={12} md={4}>
                <Field label="Lien externe (optionnel)" value={form.link} onChange={set('link')} />
              </Grid>
            )}

            {/* Description — for INFORMATION and EVENT only */}
            {(form.type === 'EVENT' || form.type === 'INFORMATION') && (
              <Grid item xs={12}>
                <Field
                  label={`Description (${300 - (form.shortDescription?.length || 0)} restants)`}
                  value={form.shortDescription}
                  onChange={(e) => {
                    const val = e.target.value.slice(0, 300);
                    setForm((p) => ({ ...p, shortDescription: val }));
                  }}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* ── Section 2: Product selection (PROMOTION + NOUVEAUTE) ── */}
        {isNewOrPromo && (
          <Box sx={{ backgroundColor: '#fff', border: '1px solid #D1D5DC', borderRadius: '12px', p: { xs: '20px', md: '20px' } }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '-4px' }}>
              Sélection des produits concernés
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#8397AE', fontWeight: 400, mb: '20px' }}>
              Choisissez comment appliquer cette promotion : par produit, catégorie ou marque
            </Typography>

            {/* ── Filter row ── */}
            <Box sx={{ display: 'flex', gap: '10px', mb: '10px' }}>
              <Box sx={{ width: '220px', flexShrink: 0 }}>
                <DropdownSelect
                  options={APPLY_TO_OPTIONS}
                  value={form.applyTo}
                  onChange={(v) => { set('applyTo')({ target: { value: v } }); setContentSearch(''); setProductPage(0); }}
                  placeholder="Appliquer à"
                  width="100%"
                  height="48px"
                  fontSize="13px"
                />
              </Box>
              <Box sx={{ flex: 1, height: '48px', display: 'flex', alignItems: 'center', border: '1px solid #DEE5EE', borderRadius: '8px', px: '12px', gap: '8px', backgroundColor: '#F8FAFC' }}>
                <img src={searchIcon} alt="search" style={{ width: 16, height: 16, flexShrink: 0 }} />
                <input
                  placeholder="Rechercher..."
                  value={form.applyTo === 'Produits' ? productSearch : contentSearch}
                  onChange={(e) => {
                    if (form.applyTo === 'Produits') { setProductSearch(e.target.value); setProductPage(0); }
                    else setContentSearch(e.target.value);
                  }}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', color: '#1A2035', width: '100%' }}
                />
              </Box>
              {form.applyTo === 'Produits' && (
                <>
                  <Box sx={{ width: '180px', flexShrink: 0 }}>
                    <DropdownSelect
                      options={[{ value: '', label: 'Toutes catégories' }, ...categories.filter((c) => c.familyLevel === 1).map((c) => ({ value: c.id, label: c.name }))]}
                      value={catFilter}
                      onChange={(v) => { setCatFilter(v); setProductPage(0); }}
                      placeholder="Catégorie"
                      width="100%"
                      height="48px"
                    />
                  </Box>
                  <Box sx={{ width: '180px', flexShrink: 0 }}>
                    <DropdownSelect
                      options={[{ value: '', label: 'Toutes marques' }, ...(brands || []).map((b) => { const n = typeof b === 'string' ? b : b.name; return { value: n, label: n }; })]}
                      value={brandFilter}
                      onChange={(v) => { setBrandFilter(v); setProductPage(0); }}
                      placeholder="Marque"
                      width="100%"
                      height="48px"
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* ── Content + panel ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              {/* table spans cols 1-3 (or full width when panel is empty) */}
              <Box sx={{ gridColumn: selectedItems.length > 0 ? 'span 3' : 'span 4', minWidth: 0 }}>
                {/* Produits table */}
                {form.applyTo === 'Produits' && (
                  <>
                    <Box sx={{ border: '1.5px solid #D1D5DB', borderRadius: '10px', width: '100%', overflow: 'hidden' }}>
                      <TableContainer>
                        <Table size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: '48px' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '42%' }} />
                            <col style={{ width: '22%' }} />
                            <col style={{ width: '18%' }} />
                          </colgroup>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#FFFFFF' }}>
                              <TableCell padding="checkbox" sx={{ borderBottom: '1.5px solid #D1D5DB', backgroundColor: '#FFFFFF' }}>
                                <Checkbox
                                  size="small"
                                  checked={products.length > 0 && products.every((p) => selectedItems.some((s) => s.ref === p.reference))}
                                  indeterminate={products.some((p) => selectedItems.some((s) => s.ref === p.reference)) && !products.every((p) => selectedItems.some((s) => s.ref === p.reference))}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const toAdd = products.filter((p) => !selectedItems.some((s) => s.ref === p.reference)).map((p) => ({ id: p.reference, name: p.name || p.reference, type: 'Produit', ref: p.reference }));
                                      setSelectedItems((prev) => [...prev, ...toAdd]);
                                    } else {
                                      setSelectedItems((prev) => prev.filter((s) => !products.some((p) => p.reference === s.ref)));
                                    }
                                  }}
                                  sx={{ color: '#D5DBE3', '&.Mui-checked': { color: '#007BC6' }, '&.MuiCheckbox-indeterminate': { color: '#007BC6' } }}
                                />
                              </TableCell>
                              {[
                                { label: 'RÉFÉRENCE', field: 'reference' },
                                { label: 'DÉSIGNATION', field: 'name' },
                                { label: 'CATÉGORIE', field: null },
                                { label: 'MARQUE', field: null },
                              ].map(({ label, field }) => {
                                const [activeField, activeDir] = sortBy.split('_');
                                const isActive = field && activeField === field;
                                const nextDir = isActive && activeDir === 'asc' ? 'desc' : 'asc';
                                return (
                                  <TableCell
                                    key={label}
                                    onClick={field ? () => { setSortBy(`${field}_${nextDir}`); setProductPage(0); } : undefined}
                                    sx={{ fontWeight: 600, fontSize: '13px', color: isActive ? '#007BC6' : '#385576', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1.5px solid #D1D5DB', backgroundColor: '#FFFFFF', py: '8px', px: '12px', cursor: field ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                                  >
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      {label}
                                      {field && (
                                        isActive
                                          ? (activeDir === 'asc' ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#007BC6' }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#007BC6', transform: 'rotate(180deg)' }} />)
                                          : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#D1D5DB' }} />
                                      )}
                                    </Box>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {productsLoading && Array.from({ length: 5 }).map((_, i) => (
                              <TableRow key={`sk-${i}`}>
                                <TableCell padding="checkbox"><Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: '3px', mx: 'auto' }} /></TableCell>
                                {[0, 1, 2, 3].map((j) => (
                                  <TableCell key={j} sx={{ py: '10px', px: '12px', borderBottom: '1.5px solid #D1D5DB' }}>
                                    <Skeleton variant="text" width="70%" height={16} />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            {!productsLoading && products.map((p, idx) => {
                              const isSel = selectedItems.some((s) => s.ref === p.reference);
                              const rowBg = isSel ? 'rgba(0, 123, 198, 0.18)' : idx % 2 === 0 ? '#FFFFFF' : '#F8FAFC';
                              const cellSx = { fontSize: '14px', fontWeight: 600, color: '#000000', py: '8px', px: '12px', borderBottom: '1.5px solid #D1D5DB', opacity: isSel ? 0.71 : 1 };
                              return (
                                <TableRow
                                  key={p.reference}
                                  selected={isSel}
                                  sx={{ cursor: 'pointer', backgroundColor: rowBg, '&.Mui-selected': { backgroundColor: 'rgba(0, 123, 198, 0.18)' }, '&:hover': { backgroundColor: isSel ? 'rgba(0, 123, 198, 0.18)' : '#EBF4FF' } }}
                                  onClick={() => {
                                    if (isSel) setSelectedItems((prev) => prev.filter((s) => s.ref !== p.reference));
                                    else setSelectedItems((prev) => [...prev, { id: p.reference, name: p.name || p.reference, type: 'Produit', ref: p.reference }]);
                                  }}
                                >
                                  <TableCell padding="checkbox" sx={{ backgroundColor: 'inherit', borderBottom: '1.5px solid #D1D5DB' }}>
                                    <Checkbox size="small" checked={isSel} sx={{ color: '#D5DBE3', '&.Mui-checked': { color: '#007BC6' } }} />
                                  </TableCell>
                                  <TableCell sx={{ ...cellSx, backgroundColor: 'inherit' }}>{p.reference}</TableCell>
                                  <TableCell sx={{ ...cellSx, backgroundColor: 'inherit' }}>{p.name}</TableCell>
                                  <TableCell sx={{ ...cellSx, backgroundColor: 'inherit' }}>{p.family?.name}</TableCell>
                                  <TableCell sx={{ ...cellSx, backgroundColor: 'inherit' }}>{p.brand?.name}</TableCell>
                                </TableRow>
                              );
                            })}
                            {!productsLoading && products.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: '24px', color: '#A0AEC0', fontSize: '13px' }}>
                                  Aucun produit trouvé
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        page={productPage + 1}
                        pageSize={productPageSize}
                        total={productTotal * productPageSize}
                        onChange={(p) => setProductPage(p - 1)}
                        pageSizeOptions={[10, 25, 50]}
                        onPageSizeChange={(s) => { setProductPageSize(s); setProductPage(0); }}
                        sx={{ borderTop: '1.5px solid #D1D5DB' }}
                      />
                    </Box>
                  </>
                )}

                {/* Catégories tree */}
                {form.applyTo === 'Catégories' && (() => {
                  const level1 = categories.filter((c) => c.familyLevel === 1);
                  const tree = level1.map((p) => ({ ...p, children: p.subFamilies || [] }));
                  const filtered = contentSearch ? tree.filter((p) => p.name?.toLowerCase().includes(contentSearch.toLowerCase()) || p.children.some((c) => c.name?.toLowerCase().includes(contentSearch.toLowerCase()))) : tree;
                  return (
                    <Box sx={{ border: '1.5px solid #D1D5DB', borderRadius: 0, overflow: 'hidden', width: '100%', backgroundColor: '#fff' }}>
                      {filtered.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: '24px', color: '#A0AEC0', fontSize: '13px' }}>Aucune catégorie trouvée</Box>
                      )}
                      {filtered.map((parent, pidx) => {
                        const isExpanded = expandedCats[parent.id];
                        const isParentSel = selectedItems.some((s) => s.id === parent.id && s.type === 'Catégorie');
                        return (
                          <Box
                            key={parent.id}
                            sx={{
                              backgroundColor: '#FFFFFF',
                              borderTop: pidx > 0 ? '1px solid #E2E8F0' : 'none',
                              borderLeft: isExpanded ? '3px solid #007BC6' : '3px solid transparent',
                            }}
                          >
                            {/* Group header row */}
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '14px', cursor: 'pointer', '&:hover': { backgroundColor: '#F8FBFF' } }}
                              onClick={() => setExpandedCats((prev) => ({ ...prev, [parent.id]: !prev[parent.id] }))}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IconButton size="small" sx={{ p: 0, color: '#007BC6' }} onClick={(e) => { e.stopPropagation(); setExpandedCats((prev) => ({ ...prev, [parent.id]: !prev[parent.id] })); }}>
                                  {isExpanded ? <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#007BC6' }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 20, color: '#007BC6' }} />}
                                </IconButton>
                                <Checkbox
                                  size="small"
                                  checked={isParentSel}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => { if (isParentSel) setSelectedItems((prev) => prev.filter((s) => !(s.id === parent.id && s.type === 'Catégorie'))); else setSelectedItems((prev) => [...prev, { id: parent.id, name: parent.name, type: 'Catégorie' }]); }}
                                  sx={{ p: '2px', color: '#D5DBE3', '&.Mui-checked': { color: '#007BC6' } }}
                                />
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#385576' }}>{parent.name}</Typography>
                              </Box>
                              {parent.totalProducts != null && (
                                <Typography sx={{ fontSize: '13px', color: '#8397AE', fontWeight: 500 }}>{parent.totalProducts} Articles</Typography>
                              )}
                            </Box>

                            {/* Expanded children table */}
                            {isExpanded && parent.children.length > 0 && (
                              <>
                                <Box sx={{ mx: '16px', borderTop: '1.5px solid #E2E8F0' }} />
                                <Box sx={{ mx: '16px', pb: '16px', mt: '8px' }}>
                                  <TableContainer>
                                    <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                      <TableBody>
                                        {parent.children.map((child, cidx) => {
                                          const isChildSel = selectedItems.some((s) => s.id === child.id && s.type === 'Catégorie');
                                          const isLast = cidx === parent.children.length - 1;
                                          const cellBorder = { borderBottom: '1.5px solid #D1D5DB', borderTop: cidx === 0 ? '1.5px solid #D1D5DB' : 'none' };
                                          return (
                                            <TableRow
                                              key={child.id}
                                              onClick={() => { if (isChildSel) setSelectedItems((prev) => prev.filter((s) => !(s.id === child.id && s.type === 'Catégorie'))); else setSelectedItems((prev) => [...prev, { id: child.id, name: child.name, type: 'Catégorie' }]); }}
                                              sx={{
                                                cursor: 'pointer',
                                                backgroundColor: isChildSel ? 'rgba(0,123,198,0.18)' : '#F8FAFC',
                                                height: '55px',
                                                '&:hover': { backgroundColor: '#EBF4FF' },
                                              }}
                                            >
                                              <TableCell padding="checkbox" sx={{ ...cellBorder, borderLeft: '1.5px solid #D1D5DB', borderRadius: isLast && cidx === 0 ? '8px 0 0 8px' : cidx === 0 ? '8px 0 0 0' : isLast ? '0 0 0 8px' : 0, px: '16px' }}>
                                                <Checkbox
                                                  size="small"
                                                  checked={isChildSel}
                                                  onChange={() => { if (isChildSel) setSelectedItems((prev) => prev.filter((s) => !(s.id === child.id && s.type === 'Catégorie'))); else setSelectedItems((prev) => [...prev, { id: child.id, name: child.name, type: 'Catégorie' }]); }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  sx={{ p: '2px', color: '#D5DBE3', '&.Mui-checked': { color: '#007BC6' } }}
                                                />
                                              </TableCell>
                                              <TableCell sx={{ ...cellBorder, fontSize: '16px', fontWeight: 600, color: '#1A2035', px: '16px' }}>{child.name}</TableCell>
                                              <TableCell sx={{ ...cellBorder, borderRight: '1.5px solid #D1D5DB', borderRadius: isLast && cidx === 0 ? '0 8px 8px 0' : cidx === 0 ? '0 8px 0 0' : isLast ? '0 0 8px 0' : 0, fontSize: '13px', fontWeight: 500, color: '#8397AE', px: '16px', textAlign: 'right' }}>
                                                {child.totalProducts != null ? `${child.totalProducts} Articles` : ''}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}

                {/* Marque grid */}
                {form.applyTo === 'Marque' && (() => {
                  const brandList = (brands || []).map((b) => (typeof b === 'string' ? { name: b } : b));
                  const filtered = contentSearch ? brandList.filter((b) => b.name?.toLowerCase().includes(contentSearch.toLowerCase())) : brandList;
                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', width: '100%' }}>
                      {filtered.length === 0 && <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: '24px', color: '#A0AEC0', fontSize: '13px' }}>Aucune marque trouvée</Box>}
                      {filtered.map((brand) => {
                        const isSel = selectedItems.some((s) => s.name === brand.name && s.type === 'Marque');
                        return (
                          <Box key={brand.name} onClick={() => { if (isSel) setSelectedItems((prev) => prev.filter((s) => !(s.name === brand.name && s.type === 'Marque'))); else setSelectedItems((prev) => [...prev, { id: brand.name, name: brand.name, type: 'Marque' }]); }} sx={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid ${isSel ? '#007BC6' : '#E2E8F0'}`, borderRadius: '10px', px: '14px', height: '72px', cursor: 'pointer', backgroundColor: isSel ? 'rgba(0,123,198,0.18)' : '#fff', '&:hover': { borderColor: '#007BC6', backgroundColor: '#F0F8FF' } }}>
                            <Checkbox size="small" checked={isSel} sx={{ p: 0, color: '#D5DBE3', '&.Mui-checked': { color: '#007BC6' } }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2 }}>{brand.name}</Typography>
                              {brandCounts[brand.name] != null && <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#8397AE', lineHeight: 1.2 }}>{brandCounts[brand.name]} Articles</Typography>}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
              </Box>

              {/* ── Right panel: selected items — col 4 ── */}
              <Box sx={{ gridColumn: 'span 1', minWidth: 0 }}>
                {selectedItems.length > 0 && (
                  <SelectedItemsPanel
                    items={selectedItems}
                    onRemove={(i) => setSelectedItems((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════
   ANNOUNCEMENTS LIST
   ════════════════════════════════════════════════════════════ */
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const AnnouncementsList = ({ onNew, onEdit, onDelete }) => {
  const { callApi } = useNetworkLoader();
  const [rawAnnouncements, setRawAnnouncements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortField, setSortField] = useState('startDate');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const debouncedSearch = useDebounce(search, 300);
  const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : '';
  const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : '';

  const load = useCallback(() => {
    setRawAnnouncements([]);
    setLoading(true);
    callApi(
      () => getAnnouncements(
        { page: page - 1, size: pageSize, sort: `${sortField},${sortDir}` },
        debouncedSearch,
      ),
      (data) => {
        setRawAnnouncements(data?.content || []);
        setTotal(data?.totalElements || 0);
        setLoading(false);
      },
    );
  }, [page, pageSize, sortField, sortDir, debouncedSearch]);

  const announcements = useMemo(() => {
    let items = rawAnnouncements;
    if (typeFilter.length > 0) items = items.filter((a) => typeFilter.includes(a.type));
    if (startDateStr) items = items.filter((a) => a.startDate && a.startDate.split('T')[0] >= startDateStr);
    if (endDateStr) items = items.filter((a) => a.endDate && a.endDate.split('T')[0] <= endDateStr);
    return items;
  }, [rawAnnouncements, typeFilter, startDateStr, endDateStr]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize]);

  const handleSort = (field) => {
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const formatD = (d) => {
    try { return format(new Date(d.replace('+0000', '+0100')), 'dd/MM/yyyy'); }
    catch { return d || ''; }
  };

  const reset = () => {
    setSearch(''); setTypeFilter([]); setStartDate(null); setEndDate(null); setPage(1);
  };

  const columns = useMemo(() => [
    {
      id: 'title',
      label: 'Titre',
      width: '20%',
      sortable: true,
      render: (row) => <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 600 }}>{row.title}</Typography>,
    },
    {
      id: 'startDate',
      label: 'Date Début',
      width: '22%',
      sortable: true,
      render: (row) => <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 600 }}>{formatD(row.startDate)}</Typography>,
    },
    {
      id: 'endDate',
      label: 'Date Fin',
      width: '20%',
      sortable: true,
      render: (row) => <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 600 }}>{formatD(row.endDate)}</Typography>,
    },
    {
      id: 'type',
      label: 'Type',
      width: '13%',
      sortable: true,
      render: (row) => <TypeBadge type={row.type} />,
    },
    {
      id: 'remise',
      label: 'Taux de Remise',
      width: '17%',
      sortable: true,
      render: (row) => (
        <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 600 }}>
          {row.remise ? `${row.remise}%` : '—'}
        </Typography>
      ),
    },
    {
      id: 'action',
      label: 'Action',
      width: '8%',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: '4px' }}>
          <Tooltip title="Modifier" placement="top">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(row); }}
              sx={{ '&:hover': { backgroundColor: '#EBF4FF' } }}
            >
              <UpdateIcon style={{ width: 20, height: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer" placement="top">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(row.id); }}
              sx={{ '&:hover': { backgroundColor: '#FFEBEE' } }}
            >
              <DeleteIcon style={{ width: 20, height: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [onEdit, onDelete]);

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', px: { xs: '16px', md: '40px' }, py: '16px' }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
            Gestion des annonces
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#385576', fontWeight: 600, mt: '-2px' }}>
            Gérez vos annonces et promotions
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={onNew}
          variant="contained"
          sx={{
            backgroundColor: '#007BC6', borderRadius: '10px', px: '20px', height: '45px',
            fontWeight: 700, fontSize: '13px', textTransform: 'none',
            '&:hover': { backgroundColor: '#005F9E' },
          }}
        >
          Créer une nouvelle annonce
        </Button>
      </Box>

      {/* Filter bar */}
      <Box sx={{
        px: '4px', py: '4px', backgroundColor: '#FFFFFF',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center',
        mb: '12px',
      }}
      >
        <Box sx={{
          flex: 1, minWidth: 220, height: '45px',
          display: 'flex', alignItems: 'center',
          border: '1px solid #DEE5EE', borderRadius: '8px',
          px: '12px', gap: '8px',
          backgroundColor: '#F8FAFC', flexShrink: 0,
        }}>
          <img src={searchIcon} alt="search" style={{ width: 20, height: 20, flexShrink: 0, objectFit: 'contain' }} />
          <input
            placeholder="Rechercher par titre ou taux de remise..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', color: '#1A2035', width: '100%' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: '120px' }}>
          <DropdownSelect
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(v) => { setTypeFilter(v); setPage(1); }}
            placeholder="Type"
            multiple
            width="100%"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: '140px' }}>
          <SingleDatePicker
            value={startDate}
            onApply={(d) => { setStartDate(d); setPage(1); }}
            placeholder="Date début"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: '140px' }}>
          <SingleDatePicker
            value={endDate}
            onApply={(d) => { setEndDate(d); setPage(1); }}
            placeholder="Date fin"
          />
        </Box>
        <AppButton
          variant="reset"
          label="Réinitialiser"
          onClick={reset}
          sx={{ height: '45px', width: '159px', flexShrink: 0 }}
        />
      </Box>

      {/* Table */}
      <CustomTable
        columns={columns}
        data={announcements}
        loading={loading}
        rowId="id"
        emptyMessage="Aucune annonce trouvée"
        sortField={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        sx={{
          border: '1.5px solid #D1D5DB',
          '& table': { borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' },
          '& thead': { backgroundColor: '#FFFFFF' },
          '& thead th': { backgroundColor: '#FFFFFF', color: '#385576', fontSize: '13px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', borderBottom: '1.5px solid #D1D5DB' },
          '& tbody tr:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
          '& tbody tr:nth-of-type(even)': { backgroundColor: '#F8FAFC' },
          '& tbody td': { borderBottom: '1.5px solid #D1D5DB' },
        }}
      />
    </Box>
  );
};


/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
const Announcements = () => {
  const { callApi } = useNetworkLoader();
  const [view, setView] = useState('list');
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [listKey, setListKey] = useState(0);

  const handleNew = () => { setEditItem(null); setView('form'); };
  const handleEdit = (ann) => { setEditItem(ann); setView('form'); };
  const handleDelete = (id) => setDeleteId(id);
  const handleDeleteConfirm = () => {
    setDeleteLoading(true);
    callApi(
      () => deleteAnnouncements(deleteId),
      () => {
        toast.success('Annonce supprimée avec succès');
        setDeleteId(null);
        setDeleteLoading(false);
        setListKey((k) => k + 1);
      },
      false,
      () => { setDeleteLoading(false); },
    );
  };
  const handleBack = () => { setView('list'); setEditItem(null); };
  const handleSaved = () => { setView('list'); setEditItem(null); setListKey((k) => k + 1); };

  return (
    <>
      <Box sx={{ display: view === 'list' ? 'block' : 'none' }}>
        <AnnouncementsList
          key={listKey}
          onNew={handleNew}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>
      {view === 'form' && (
        <AnnouncementForm
          editItem={editItem}
          onBack={handleBack}
          onSaved={handleSaved}
        />
      )}
      {deleteId && (
        <ConfirmDialog
          title="Confirmer la suppression"
          content="Êtes-vous sûr de vouloir supprimer cette annonce ? Cette action est irréversible."
          handleClose={() => { if (!deleteLoading) setDeleteId(null); }}
          handleClick={handleDeleteConfirm}
          loading={deleteLoading}
        />
      )}
    </>
  );
};

export default withRoles('ROLE_ADMIN')(Announcements);
